import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../core/services/language.service';
import {
	Icons,
	IFilter,
	IMenu,
	ISelect,
	ITab,
	ITableCell,
	ITableConfig,
	ITableHeader,
	ITableRow,
	ITableSortEvent,
	ITimelineItem,
	IToolbar,
} from '@tecalisdev/infinity';
import { buildIdentityCell } from '../../core/utils/build-identity-cell.util';
import {
	formatDate,
	formatDuration,
	notAvailableLabel,
	permanentExpirationLabel,
	transformDate,
	truncateUUID,
	withNationalityFlag,
} from '../../core/utils/utils';
import { createDateRangeFilters } from '../../shared/list/create-date-range-filters.util';
import {
	BOOLEAN_STATUS,
	IOperationDocumentsDetail,
	IOperationEvaluationDetail,
	IOperationOcrDetail,
	IOperationPolDataTry,
	IOperationPolDetail,
	IPolGeneralData,
	IProcessActivity,
	IProcessDetailResponse,
	IProcessListBody,
	IProcessListOperation,
	isPolDataRowKoMultipleFaces,
	PROCESS_STATUS,
	TAB_PROCESS,
} from '../models/processes.models';
import { MasterService } from '../../shared/services/master.service';
import {
	CONNECTION_TYPE,
	IOperationFormDetail,
	OPERATION_STATUS,
	OPERATION_TYPE,
} from '../../operations/models/operations.models';
import { appendFormOperationActivityLogs } from '../../operations/utils/form-operation-activity.util';
import { appendDocumentsActivityLogs } from '../../operations/utils/documents-operation-activity.util';
import { appendFinancialEvaluationActivityLogs } from '../../operations/utils/financial-evaluation-operation-activity.util';
import { appendOcrActivityLogs } from '../../operations/utils/ocr-operation-activity.util';
import { appendPolActivityLogs } from '../../operations/utils/pol-operation-activity.util';
import { hasFormOperationMatchDataKo } from '../../operations/utils/form-operation-match-data.util';
import { resolveMatchDataUserMessageFromKoError } from '../../shared/utils/match-data-user-message.util';
import { MenuItemAction } from '../../shared/models/master.models';
import { Permission } from '../../core/models/role-permissions.models';
import { RolePermissionsService } from '../../core/services/role-permissions.service';
import { Router } from '@angular/router';
import { CompaniesService } from '../../settings/pages/companies/services/companies.service';
import { WorkflowsService } from '../../workflows/services/workflows.service';
import { IWorkflowsListBody } from '../../workflows/models/workflows.models';
import { ErrorsNumbersService } from '../../settings/pages/error-catalog/services/errors-numbers.service';
import { buildUnifiedPolicyItems } from '../../core/utils/policy-check.utils';
import { environment } from '../../../environments/environment';
import {
	hasDisplayableOcrCompareAntifraud as hasDisplayableOcrCompareAntifraudUtil,
	shouldDisplayAntifraudCheck as shouldDisplayAntifraudCheckUtil,
	shouldShowOperationKoReason as shouldShowOperationKoReasonUtil,
} from './process-helper-checks.util';
import {
	isPolGeneralResultOk as isPolGeneralResultOkUtil,
	polSelfiesForAdminPreview as polSelfiesForAdminPreviewUtil,
} from './process-helper-pol.util';

/** Mensaje del API → clave `PROCESSES.DETAIL.*` (traducción en en.json / es.json). */
const KO_COMPLETION_BACKEND_TO_I18N: Record<string, string> = {
	'Operation marked as KO due to pending state on process completion': 'PROCESSES.DETAIL.KO_COMPLETION_PENDING_STATE',
	'KO: a prior operation failed while this step was still active.':
		'PROCESSES.DETAIL.KO_COMPLETION_PRIOR_OPERATION_FAILED',
};

/** Valores que deben restaurarse en el toolbar (excluye null, undefined, '' y arrays vacíos). */
function isToolbarPayloadValuePresent(v: any): boolean {
	if (v == null) return false;
	if (typeof v === 'string' && v.trim() === '') return false;
	if (Array.isArray(v) && v.length === 0) return false;
	return true;
}

@Injectable({
	providedIn: 'root',
})
export class ProcessHelperService {
	trans = inject(TranslateService);
	masterService = inject(MasterService);
	rolePermissions = inject(RolePermissionsService);
	router = inject(Router);
	private langSvc = inject(LanguageService);

	public yearsOld: number = 0;
	public ocrDocumentAgeKnown = false;
	private errorsNumbersService = inject(ErrorsNumbersService);
	private companiesService = inject(CompaniesService);
	private workflowsService = inject(WorkflowsService);

	/** OK visual del resultado general POL: deben cumplirse scoring y similitud global. */
	public isPolGeneralResultOk(general: IPolGeneralData | undefined): boolean {
		return isPolGeneralResultOkUtil(general);
	}

	/**
	 * Selfies visibles en admin para POL (NIF-822).
	 * Activa (`mode === 'auto'`): sin cambio de UI — hasta 2 (como el preview histórico).
	 * Estática: solo la oficial (con ≥3 URLs en orden DESC del back → índice 1; si no → índice 0).
	 */
	public polSelfiesForAdminPreview(
		selfie: string[] | undefined,
		mode: string | undefined,
	): string[] {
		return polSelfiesForAdminPreviewUtil(selfie, mode);
	}

	/**
	 * Frames de buffer antispoof de POL estática (NIF-971), complemento de `polSelfiesForAdminPreview`.
	 * Solo para preview debug; activa (`auto`) → [].
	 */
	public polSilentFramesForAdminDebug(
		selfie: string[] | undefined,
		mode: string | undefined,
	): string[] {
		if (!selfie?.length || mode === 'auto') {
			return [];
		}
		if (selfie.length >= 3) {
			return selfie.filter((_, i) => i !== 1);
		}
		if (selfie.length === 2) {
			return [selfie[1]];
		}
		return [];
	}

	private metadataKeys = signal<ISelect[]>([]);
	private workflowOptions = signal<ISelect[]>([]);
	private workflowsCatalog = signal<Array<ISelect & { company_uuid?: string }>>([]);
	private workflowsCatalogLoading = false;
	private pendingWorkflowCompanyUuid: string | string[] | null | undefined;
	private filterValues = signal<Record<string, any>>({});
	private availableKoErrorCodes = signal<number[]>([]);

	// Single computed instance created once — toolbar is a field, not a getter,
	// so the component always observes the same Signal reference.

	readonly toolbar = computed<IToolbar>(() => {
		this.langSvc.currentLang(); // dependencia reactiva: recalcula al cambiar idioma
		const vals = this.filterValues();
		const filters: IFilter[] = [
			/*
			{
				type: 'select',
				title: this.trans.instant('FILTERS.STATE'),
				name: 'state_uuid',
				placeholder: this.trans.instant('FILTERS.STATE_PLACEHOLDER'),
				placeholderThin: this.trans.instant('FILTERS.STATE'),
				options: this.masterService.processStatusFilter,
				value: vals['state_uuid'],
			},
			*/
			{
				type: 'select',
				title: this.trans.instant('FILTERS.TYPE'),
				name: 'document_type',
				placeholder: this.trans.instant('FILTERS.DOCUMENT_TYPE_PLACEHOLDER'),
				placeholderThin: this.trans.instant('FILTERS.TYPE'),
				options: this.masterService.documentTypes,
				value: vals['document_type'],
			},
			...createDateRangeFilters({
				fromTitle: this.trans.instant('FILTERS.LAST_UPDATE'),
				toTitle: this.trans.instant('FILTERS.TO'),
				fromPlaceholder: this.trans.instant('FILTERS.FROM'),
				fromLabelChip: this.trans.instant('FILTERS.FROM'),
				fromValue: vals['start_at'],
				toValue: vals['end_at'],
			}).map((f) => ({ ...f, datepickerType: 'date-time' as any })),
			{
				type: 'select',
				title: this.trans.instant('FILTERS.NACIONALITY'),
				name: 'nationality',
				placeholder: this.trans.instant('FILTERS.NACIONALITY_PLACEHOLDER'),
				placeholderThin: this.trans.instant('FILTERS.NACIONALITY'),
				options: this.masterService.countries,
				search: true,
				value: vals['nationality'],
			},
			{
				type: 'select',
				title: this.trans.instant('FILTERS.OPERATIONS'),
				name: 'operation_type_uuids',
				placeholder: this.trans.instant('FILTERS.TYPE_PLACEHOLDER'),
				placeholderThin: this.trans.instant('FILTERS.OPERATIONS'),
				options: this.masterService.operationsTypes,
				multiple: true,
				value: vals['operation_type_uuids'],
			},
			{
				type: 'select',
				title: this.trans.instant('FILTERS.BROWSER'),
				name: 'browser_uuids',
				placeholder: this.trans.instant('FILTERS.BROWSER_PLACEHOLDER'),
				placeholderThin: this.trans.instant('FILTERS.BROWSER'),
				options: this.masterService.browsers,
				multiple: true,
				search: true,
				value: vals['browser_uuids'],
			},
			{
				type: 'select',
				title: this.trans.instant('FILTERS.DEVICE_TYPE'),
				name: 'device_type_uuids',
				placeholder: this.trans.instant('FILTERS.DEVICE_TYPE_PLACEHOLDER'),
				placeholderThin: this.trans.instant('FILTERS.DEVICE_TYPE'),
				options: this.masterService.deviceTypes,
				multiple: true,
				search: true,
				value: vals['device_type_uuids'],
			},
			{
				type: 'text',
				title: this.trans.instant('FILTERS.PROCESS_ID'),
				name: 'process_uuid',
				placeholder: this.trans.instant('FILTERS.PROCESS_ID_PLACEHOLDER'),
				value: vals['process_uuid'],
			},
			{
				type: 'text',
				title: this.trans.instant('FILTERS.AUTHORIZATION_ID'),
				name: 'authorization_uuid',
				placeholder: this.trans.instant('FILTERS.AUTHORIZATION_ID_PLACEHOLDER'),
				value: vals['authorization_uuid'],
			},
			{
				type: 'text',
				title: this.trans.instant('FILTERS.DOCUMENT_NUMBER'),
				name: 'document_number',
				placeholder: this.trans.instant('FILTERS.DOCUMENT_NUMBER_PLACEHOLDER'),
				value: vals['document_number'],
			},
			{
				type: 'select',
				title: this.trans.instant('FILTERS.METADATA_KEY'),
				name: 'metadata_key',
				placeholder: this.trans.instant('FILTERS.METADATA_KEY_PLACEHOLDER'),
				options: this.metadataKeys(),
				search: true,
				value: vals['metadata_key'],
			},
			{
				type: 'text',
				title: this.trans.instant('FILTERS.METADATA_VALUE'),
				name: 'metadata_value',
				placeholder: this.trans.instant('FILTERS.METADATA_VALUE_PLACEHOLDER'),
				value: vals['metadata_value'],
			},
		];

		// Si hay más de una compañía o es superadmin, agregar filtro de compañía en la posición 3
		const hasCompanySelector = this.masterService.companies.length > 1 || this.rolePermissions.isSuperadmin();
		if (hasCompanySelector) {
			filters.splice(3, 0, {
				type: 'select',
				title: this.trans.instant('FILTERS.COMPANY'),
				name: 'company_uuid',
				placeholder: this.trans.instant('FILTERS.COMPANY_PLACEHOLDER'),
				placeholderThin: this.trans.instant('FILTERS.COMPANY'),
				options: this.masterService.companies,
				search: true,
				multiple: true,
				value: vals['company_uuid'],
			});
		}

		const companyFilterIndex = filters.findIndex((f) => f.name === 'company_uuid');
		const workflowInsertAt = companyFilterIndex >= 0 ? companyFilterIndex + 1 : 3;
		filters.splice(workflowInsertAt, 0, {
			type: 'select',
			title: this.trans.instant('FILTERS.WORKFLOW'),
			name: 'workflow_uuid',
			placeholder: this.trans.instant('FILTERS.WORKFLOW_PLACEHOLDER'),
			placeholderThin: this.trans.instant('FILTERS.WORKFLOW'),
			options: this.workflowOptions(),
			search: true,
			value: vals['workflow_uuid'],
		});

		return {
			filters,
			fastFilters: this.masterService.processStatusFilter
				? [
						{
							name: 'state_uuid',
							options: this.masterService.processStatusFilter,
							// Debe reflejar filterValues; si no, al recrear el listado (p. ej. volver del detalle) los chips quedan vacíos pese a setFiltersFromPayload.
							value: vals['state_uuid'] ?? [],
							multiple: true,
						},
					]
				: undefined,
			search: {
				placeholder: this.trans.instant('FILTERS.SEARCH_BY_NAME_OR_DOCUMENT'),
				name: 'search',
			},
			...(this.rolePermissions.hasPermission(Permission.VIEW_DEVELOPER_ACTION)
				? {
						action: {
							id: MenuItemAction.SHOW_CURL,
							icon: 'icon-code-brackets',
							tooltip: this.trans.instant('GENERAL.CURL_REQUEST'),
						},
					}
				: {}),
		};
	});

	get header(): ITableHeader[] {
		const headers: ITableHeader[] = [
			{ label: this.trans.instant('TABLE.HEADER.PROCESS_ID'), sortable: true, sortKey: 'process_uuid' },
			{ label: this.trans.instant('TABLE.HEADER.NAME'), sortable: true, sortKey: 'name' },
			{ label: this.trans.instant('TABLE.HEADER.DOCUMENT_NUMBER') },
			{ label: this.trans.instant('TABLE.HEADER.NACIONALITY') },
			{ label: this.trans.instant('TABLE.HEADER.COMPANY') },
			{ label: this.trans.instant('TABLE.HEADER.OPERATIONS') },
			{ label: this.trans.instant('TABLE.HEADER.STATE') },
			{
				label: this.trans.instant('TABLE.HEADER.DATE_CREATE_ABREV'),
				sortable: true,
				sortKey: 'created_at',
				align: 'right',
			},
		];

		// Añadir columna de acciones si el usuario puede eliminar procesos
		if (this.rolePermissions.canDeleteProcesses()) {
			headers.push({ label: '' });
		}

		return headers;
	}

	get config(): ITableConfig {
		return {
			clickeable: true,
			pagination: {
				page: 1,
				limit: 25,
				total: 0,
				label: this.trans.instant('TABLE.ELEMENTS_BY_PAGE'),
				elementsByPage: [
					{ id: '25', label: '25' },
					{ id: '50', label: '50' },
					{ id: '100', label: '100' },
				],
			},
		};
	}

	get sort(): ITableSortEvent {
		return { ...this.resetSort() };
	}

	public resetSort(): ITableSortEvent {
		return {
			sortKey: 'created_at',
			sortDirection: 'desc',
			headerIndex: this.header.findIndex((h) => h.sortKey === 'created_at') || 0,
		};
	}

	/** Cabeceras reducidas: id proceso, workflow, operaciones, estado, última actividad (sin ordenación en UI). */
	get identityDetailProcessesHeader(): ITableHeader[] {
		return [
			{ label: this.trans.instant('IDENTITIES.DETAIL.ID_PROCESS') },
			{ label: this.trans.instant('PROCESSES.DETAIL.WORKFLOW') },
			{ label: this.trans.instant('TABLE.HEADER.OPERATIONS') },
			{ label: this.trans.instant('TABLE.HEADER.STATE') },
			{
				label: this.trans.instant('TABLE.HEADER.DATE_LAST_UPDATE_ABREV'),
				align: 'right',
			},
		];
	}

	get tabs(): ITab[] {
		return [
			{
				id: TAB_PROCESS.OPERATIONS,
				label: this.trans.instant('PROCESSES.DETAIL.TABS.OPERATIONS'),
				selected: true,
			},
			{ id: TAB_PROCESS.CHECKS, label: this.trans.instant('PROCESSES.DETAIL.TABS.CHECKS') },
			{ id: TAB_PROCESS.ACTIVITY, label: this.trans.instant('PROCESSES.DETAIL.TABS.ACTIVITY') },
			{ id: TAB_PROCESS.DETAILS, label: this.trans.instant('PROCESSES.DETAIL.TABS.DETAILS') },
		];
	}

	public transformRow(response: IProcessListBody[]): ITableRow[] {
		return response.map((row) => this.transformListTableRow(row));
	}

	public transformListTableRow(row: IProcessListBody): ITableRow {
		const timeline: ITimelineItem[] = this.timelineOperations(row.operations);
		const empty = notAvailableLabel(this.trans);
		const cells: ITableCell[] = [
			{ label: truncateUUID(row.process_uuid), copyText: { labelToCopy: row.process_uuid } },
			{ label: row?.name?.trim() || empty },
			buildIdentityCell(row, this.trans, (type) => this.masterService.getDocumentTypeLabel(type)),
			{
				label: withNationalityFlag(
					row.nationality,
					this.masterService.hasTranslation(`MASTER.COUNTRIES.${row.nationality}`)
						? this.trans.instant(`MASTER.COUNTRIES.${row.nationality}`)
						: empty,
				),
			},
			{ label: row.company },
			{
				label: timeline?.length > 0 ? '' : empty,
				timeline: timeline?.length > 0 ? timeline : undefined,
			},
			{
				label: '',
				tag: {
					label: this.resolveProcessStatusLabel(row.status, row.details_code),
					status: this.masterService.getTagStatus(row.status),
				},
			},
			{ label: formatDate(row.created_at), align: 'right' },
		];

		// Añadir botón/menú de acciones si tiene permiso de eliminación
		if (this.rolePermissions.canDeleteProcesses()) {
			const menu: IMenu[] = [
				{
					id: MenuItemAction.DELETE,
					icon: 'icon-trash',
					label: this.trans.instant('BUTTON.DELETE'),
					style: 'red',
				},
			];

			cells.push({
				label: '',
				align: 'right',
				buttonIcon: {
					id: 'process-actions',
					icon: 'icon-more',
					menu,
				},
			});
		}

		return {
			clickValue: row.process_uuid,
			cells,
		};
	}

	/** Filas para la tabla compacta de procesos en ficha de identidad. */
	public transformIdentityDetailProcessesRows(rows: IProcessListBody[]): ITableRow[] {
		return rows.map((row) => this.transformIdentityDetailProcessRow(row));
	}

	private transformIdentityDetailProcessRow(row: IProcessListBody): ITableRow {
		const timeline: ITimelineItem[] = this.timelineOperations(row.operations);
		const empty = notAvailableLabel(this.trans);
		const cells: ITableCell[] = [
			{
				label: `<a [routerLink]="['/processes', 'detail', '${row.process_uuid}']">${truncateUUID(
					row.process_uuid,
				)}</a>`,
			},
			{ label: (row.profile_alias ?? '').trim() || empty },
			{
				label: timeline?.length > 0 ? '' : empty,
				timeline: timeline?.length > 0 ? timeline : undefined,
			},
			{
				label: '',
				tag: {
					label: this.resolveProcessStatusLabel(row.status, row.details_code),
					status: this.masterService.getTagStatus(row.status),
				},
			},
			{ label: formatDate(row.updated_at), align: 'right' },
		];

		return {
			clickValue: row.process_uuid,
			cells,
		};
	}

	/** Timeline de operaciones para listados (panel incluido); incluye extraInfo para hover. */
	public timelineOperations(operations: IProcessListOperation[] | null | undefined): ITimelineItem[] {
		if (!operations?.length) return [];
		return operations
			.filter((op: IProcessListOperation) => this.isListTimelineOperationType(op.type))
			.map((op: IProcessListOperation) => {
				const type = op.type?.toUpperCase() ?? '';
				const titleKey = this.listOperationTitleKey(type);
				const contentItems = this.timelineRejectionContentItems(op);
				return {
					icon: this.getOperationIcon(type),
					status: this.getTimelineStatus(op.state as any as OPERATION_STATUS),
					title: this.trans.instant(titleKey),
					extraInfo: {
						infoTitle: this.trans.instant(titleKey),
						tag: {
							label: this.trans.instant(`MASTER.STATUS.${op.state?.toUpperCase()}`),
							status: this.getTimelineStatus(op.state as any as OPERATION_STATUS),
						},
						detail:
							op.total_time !== null && op.total_time !== undefined
								? formatDuration(op.total_time)
								: undefined,
						link: op.operation
							? { href: `/operations/detail/${op.operation}`, target: '_self' }
							: undefined,
						...(contentItems.length
							? {
									contentTitle: this.trans.instant('PROCESSES.LIST.REJECTION_REASON_TITLE'),
									contentItems,
								}
							: {}),
					},
				};
			});
	}

	/**
	 * NIF-765: entradas del popover «Motivo rechazo» (Infinity contentItems).
	 * Si hay `code`, usa la misma copy i18n que Resultado (`detailKoGeneral`).
	 */
	private timelineRejectionContentItems(
		op: IProcessListOperation,
	): Array<{ label: string; value: string }> {
		const reasons = op.rejection_reasons;
		if (!reasons?.length) {
			return [];
		}
		const state = String(op.state ?? '').toUpperCase();
		if (state !== PROCESS_STATUS.KO && state !== PROCESS_STATUS.CANCELLED) {
			return [];
		}

		const items: Array<{ label: string; value: string }> = [];
		for (const reason of reasons) {
			const code = typeof reason.code === 'number' && reason.code > 0 ? reason.code : 0;
			if (code > 0) {
				const ko = this.detailKoGeneral(code, op.type as OPERATION_TYPE, undefined, {
					title: reason.title,
					message: reason.message,
				});
				items.push({
					label: ko.title,
					value: ko.subtitle || '',
				});
				continue;
			}
			const label = (reason.label ?? '').trim();
			if (!label) {
				continue;
			}
			items.push({
				label,
				value: (reason.value ?? '').trim(),
			});
		}
		return items;
	}

	private isListTimelineOperationType(type: string | null | undefined): boolean {
		const normalized = type?.toUpperCase();
		return (
			normalized === OPERATION_TYPE.OCR ||
			normalized === OPERATION_TYPE.FORM ||
			normalized === OPERATION_TYPE.DOCUMENTS ||
			normalized === OPERATION_TYPE.POL ||
			normalized === OPERATION_TYPE.CONNECTIONS ||
			normalized === OPERATION_TYPE.ATTORNEY_POWERS ||
			normalized === OPERATION_TYPE.EVALUATION ||
			normalized === OPERATION_TYPE.FINANCIAL_EVALUATION ||
			normalized === OPERATION_TYPE.TFA
		);
	}

	private listOperationTitleKey(type: string): string {
		if (type === OPERATION_TYPE.ATTORNEY_POWERS) {
			return 'WORKFLOWS.FORM.OPERATIONS.ATTORNEY_POWERS';
		}
		if (
			type === OPERATION_TYPE.CONNECTIONS ||
			type === OPERATION_TYPE.EVALUATION ||
			type === OPERATION_TYPE.FINANCIAL_EVALUATION
		) {
			return 'WORKFLOWS.FORM.OPERATIONS.CONNECTIONS';
		}
		return `PROCESSES.DETAIL.${type}`;
	}

	public truncateUUID(uuid: string): string {
		return truncateUUID(uuid);
	}

	/**
	 * Opciones del menú JSON - Solo visible para SuperAdmin
	 */
	get dataJsonMenuOpt(): IMenu[] {
		if (!this.rolePermissions.isSuperadmin()) return [];

		return [
			{
				label: this.trans.instant('BUTTON.SEE'),
				icon: 'icon-eye',
				id: MenuItemAction.SHOW,
			},
			{
				label: this.trans.instant('BUTTON.COPY'),
				icon: 'icon-copy',
				id: MenuItemAction.COPY,
			},
			{
				label: this.trans.instant('BUTTON.DOWNLOAD'),
				icon: 'icon-download',
				id: MenuItemAction.DOWNLOAD,
			},
		];
	}

	public detailMenuOpt(detail: IProcessDetailResponse, showChageState: boolean = true): IMenu[] {
		const menuItems: IMenu[] = [];

		const status = detail?.status_process;
		const auditDownloadable =
			status === PROCESS_STATUS.OK ||
			status === PROCESS_STATUS.KO ||
			status === PROCESS_STATUS.CANCELLED;

		if (auditDownloadable) {
			const signedPdfs = Boolean(detail?.general?.signed_pdfs);
			if (signedPdfs) {
				menuItems.push({
					label: this.trans.instant('PROCESSES.DETAIL.AUDIT_DOWNLOAD_SIGNED'),
					icon: 'icon-shield',
					id: MenuItemAction.AUDIT_SIGNED,
				});
			} else {
				menuItems.push({
					label: this.trans.instant('PROCESSES.DETAIL.AUDIT_DOWNLOAD_SUMMARY'),
					icon: 'icon-download',
					id: MenuItemAction.AUDIT_SUMMARY,
				});
			}
		}

		// Si es superadmin, ignorar el showChageState
		if (this.rolePermissions.isSuperadmin() || this.rolePermissions.isAdmin()) showChageState = true;

		// Cambiar estado - requiere permiso PROCESSES_CHANGE_STATUS
		if (this.rolePermissions.canChangeProcessStatus() && showChageState) {
			menuItems.push({
				label: this.trans.instant('BUTTON.CHANGE_STATUS'),
				icon: 'icon-edit',
				id: MenuItemAction.STATUS,
			});
		}

		// Bloquear identidad - requiere permiso PERSONS_BLOCK
		if (
			this.rolePermissions.canBlockPersons() &&
			(!detail?.locks?.blocked_ips ||
				(detail?.identity?.identifier && !detail?.locks?.blocked_identifiers) ||
				(detail?.data?.POL && !detail?.locks?.blocked_faces))
		) {
			menuItems.push({
				label: this.trans.instant('PROCESSES.MENU.BLOCK_IDENTITY'),
				icon: 'icon-lock',
				id: MenuItemAction.BLOCK,
			});
		}
		// Desbloquear identidad - requiere permiso PERSONS_UNBLOCK
		if (this.rolePermissions.canUnblockPersons() && detail?.locks) {
			menuItems.push({
				label: this.trans.instant('PROCESSES.MENU.UNLOCK_IDENTITY'),
				icon: 'icon-unlock',
				id: MenuItemAction.UNLOCK,
			});
		}

		if (this.rolePermissions.hasPermission(Permission.VIEW_DEVELOPER_ACTION)) {
			menuItems.push({
				label: this.trans.instant('GENERAL.CURL_REQUEST'),
				icon: 'icon-code-brackets',
				id: MenuItemAction.SHOW_CURL,
			});
		}

		if (this.rolePermissions.isSuperadmin() && detail?.logs_url) {
			menuItems.push({
				label: this.trans.instant('PROCESSES.DETAIL.LOGS'),
				icon: 'icon-open-web',
				id: MenuItemAction.SHOW_LOGS,
			});
		}

		return menuItems;
	}

	public setTimeline(detail: IProcessDetailResponse): void {
		detail.timeline = [];
		for (const operationKey of Object.keys(detail?.data ?? {})) {
			const operation = detail.data[operationKey as keyof typeof detail.data];
			if (operationKey !== 'HOME' && operationKey !== 'FINISH' && operation) {
				const evaluationType =
					operationKey === OPERATION_TYPE.EVALUATION
						? (operation as IOperationEvaluationDetail).type?.toUpperCase()
						: undefined;
				const isAttorneyPowers =
					operationKey === OPERATION_TYPE.ATTORNEY_POWERS || evaluationType === CONNECTION_TYPE.SCRIPTURE;
				const titleKey = isAttorneyPowers
					? 'WORKFLOWS.FORM.OPERATIONS.ATTORNEY_POWERS'
					: `PROCESSES.DETAIL.${operationKey}`;
				detail.timeline.push({
					status: this.getTimelineStatus(operation.state as OPERATION_STATUS),
					icon: isAttorneyPowers ? 'icon-shield-check' : this.getOperationIcon(operationKey),
					title: this.trans.instant(titleKey),
					extraInfo: {
						infoTitle: this.trans.instant(titleKey),
						tag: {
							label: this.trans.instant(`MASTER.STATUS.${operation.state?.toUpperCase()}`),
							status: this.getTimelineStatus(operation.state as OPERATION_STATUS),
						},
						detail:
							operation.total_time !== null && operation.total_time !== undefined
								? formatDuration(operation.total_time)
								: undefined,
						// link: `/operations/detail/${operation.uuid}`,
					},
				});
			}
		}
	}

	private getTimelineItem(
		operation: IOperationPolDetail | IOperationOcrDetail | IOperationDocumentsDetail | IOperationEvaluationDetail,
		operationKey: string,
	): ITimelineItem {
		if (operation.state === OPERATION_STATUS.INIT) {
			return {
				status: this.getTimelineStatus(operation.state),
				icon: this.getOperationIcon(operationKey),
				title: this.trans.instant(`PROCESSES.DETAIL.${operationKey}`),
				extraInfo: {
					detail: this.trans.instant('GENERAL.NO_DATA'),
				},
			};
		}

		return {
			status: this.getTimelineStatus(operation.state),
			icon: this.getOperationIcon(operationKey),
			title: this.trans.instant(`PROCESSES.DETAIL.${operationKey}`),
			extraInfo: {
				infoTitle: this.trans.instant(`PROCESSES.DETAIL.${operationKey}`),
				tag: {
					label: this.trans.instant(`MASTER.STATUS.${operation.state?.toUpperCase()}`),
					status: this.getTimelineStatus(operation.state),
				},
				detail:
					operation.total_time !== null && operation.total_time !== undefined
						? formatDuration(operation.total_time)
						: undefined,
				// link: `/operations/detail/${operation.uuid}`,
			},
		};
	}

	private getTimelineStatus(operationState: OPERATION_STATUS): any {
		switch (operationState) {
			case OPERATION_STATUS.OK:
				return 'success';
			case OPERATION_STATUS.KO:
			case OPERATION_STATUS.CANCELLED:
				return 'error';
			case OPERATION_STATUS.INIT:
			case OPERATION_STATUS.NOT_STARTED:
				return 'empty';
			case OPERATION_STATUS.IN_PROGRESS:
				return 'default';
			default:
				return 'empty';
		}
	}

	private getOperationIcon(operation: string): Icons {
		switch (operation.toUpperCase()) {
			case 'TFA':
				return 'icon-smartphone';
			case OPERATION_TYPE.OCR:
				return 'icon-card';
			case OPERATION_TYPE.POL:
				return 'icon-user';
			case OPERATION_TYPE.DOCUMENTS:
				return 'icon-file';
			case OPERATION_TYPE.FORM:
				return 'icon-fill-form';
			case OPERATION_TYPE.CONNECTIONS:
			case OPERATION_TYPE.EVALUATION:
			case OPERATION_TYPE.FINANCIAL_EVALUATION:
				return 'icon-euro';
			case OPERATION_TYPE.ATTORNEY_POWERS:
				return 'icon-shield-check';
		}
		return 'icon-card';
	}

	private operationNeverStarted(operation: { start_at?: string | null } | null | undefined): boolean {
		const s = operation?.start_at;
		if (s == null) {
			return true;
		}
		return typeof s === 'string' && s.trim() === '';
	}

	private pushActivitySectionAndNotStarted(
		detail: IProcessDetailResponse,
		sectionTitleKey: string,
		opts: {
			operation: unknown;
			linkInfoTitleKey: string;
			state?: OPERATION_STATUS;
			date?: string | null;
			showLogs?: boolean;
		},
	): void {
		detail.activity.push({
			section: true,
			title: this.trans.instant(sectionTitleKey)?.toUpperCase(),
		});
		const row: IProcessActivity = {
			description: [this.trans.instant('PROCESSES.DETAIL.OPERATION_NOT_STARTED')],
			...(opts.state
				? {
						tag: {
							label: this.trans.instant(`MASTER.STATUS.${opts.state.toUpperCase()}`),
							status: this.masterService.getTagStatus(opts.state),
						},
					}
				: {}),
			...(opts.date ? { date: opts.date } : {}),
			link: {
				label: this.trans.instant('GENERAL.SHOW_RESPONSE'),
				action: MenuItemAction.SHOW_JSON,
				info: { title: this.trans.instant(opts.linkInfoTitleKey), data: opts.operation },
				...(opts.showLogs ? { showLogs: true } : {}),
			},
		};
		detail.activity.push(row);
	}

	/**
	 * NIF-800: actividad BORME/poderes — una fila por intento auto (connections) y por subida manual (document).
	 */
	private pushAttorneyPowersAttemptActivityRows(
		detail: IProcessDetailResponse,
		evaluation: IOperationEvaluationDetail,
	): void {
		const typeTitle = this.masterService.hasTranslation(
			'FINANCIAL_EVALUATION.' + evaluation.type?.toUpperCase(),
		)
			? this.trans.instant('FINANCIAL_EVALUATION.' + evaluation.type?.toUpperCase())
			: notAvailableLabel(this.trans);

		const connections = evaluation.connections ?? [];
		connections.forEach((conn) => {
			const description: string[] = [this.trans.instant('PROCESSES.DETAIL.AUTOMATIC')];
			const errorLabel = this.resolveAttorneyPowersAttemptErrorLabel(conn.details, conn.error_code);
			if (errorLabel) {
				description.push(errorLabel);
			}
			detail.activity.push({
				title: typeTitle,
				icon: 'icon-connection',
				status: (conn.state ?? evaluation.state) as unknown as BOOLEAN_STATUS,
				description,
				date: conn.created_at,
			});
		});

		const documents = evaluation.document ?? [];
		documents.forEach((doc) => {
			const description: string[] = [this.trans.instant('PROCESSES.DETAIL.MANUAL')];
			const errorLabel = this.resolveAttorneyPowersAttemptErrorLabel(doc.details, doc.error_code);
			if (errorLabel) {
				description.push(errorLabel);
			}
			detail.activity.push({
				title: this.masterService.hasTranslation('DOCS.SCRIPTURE')
					? this.trans.instant('DOCS.SCRIPTURE')
					: typeTitle,
				icon: 'icon-file',
				status: (doc.state ?? evaluation.state) as unknown as BOOLEAN_STATUS,
				description,
				date: doc.created_at,
			});
		});

		if (connections.length === 0 && documents.length === 0) {
			const description: string[] = [];
			const errorLabel = this.resolveAttorneyPowersAttemptErrorLabel(evaluation.details, null);
			if (errorLabel) {
				description.push(errorLabel);
			} else if (evaluation.scoring) {
				description.push(this.trans.instant('PROCESSES.DETAIL.SCORING') + ': ' + evaluation.scoring);
			}
			detail.activity.push({
				title: typeTitle,
				icon: evaluation.mode === 'manual' ? 'icon-file' : 'icon-connection',
				status: evaluation?.state as unknown as BOOLEAN_STATUS,
				description,
			});
		}
	}

	/**
	 * NIF-800: código a mostrar en Resultado — preferir business_error_code (motivo) sobre wrap 2024/2007.
	 */
	public resolveAttorneyPowersKoDisplayCode(
		evaluation: IOperationEvaluationDetail | null | undefined,
	): number | null {
		const err = evaluation?.actions?.KO?.details?.error;
		const business = err?.business_error_code;
		if (business != null && business !== 0) {
			return Number(business);
		}
		const code = err?.error_code;
		if (code != null && code !== 0) {
			return Number(code);
		}
		return null;
	}

	/** Etiqueta i18n del intento; sin prefijo «Poderes - » (filas internas de actividad). */
	private resolveAttorneyPowersAttemptErrorLabel(
		details: string | { error_code?: number; msg?: string } | null | undefined,
		errorCode?: number | null,
	): string | null {
		let code = errorCode != null && errorCode !== 0 ? Number(errorCode) : null;
		let msg: string | null = null;

		if (details && typeof details === 'object') {
			if (code == null && details.error_code != null) {
				code = Number(details.error_code);
			}
			if (details.msg) {
				msg = String(details.msg);
			}
		} else if (typeof details === 'string' && details.trim() !== '') {
			msg = details.trim();
		}

		if (code != null && !Number.isNaN(code)) {
			const titleKey = `ERRORS.PROCESS.TITLE_${code}`;
			const translated = this.trans.instant(titleKey);
			if (translated && translated !== titleKey) {
				return translated;
			}
			return this.detailKoGeneral(code).title;
		}

		if (msg) {
			return msg;
		}
		return null;
	}

	public setActivityLogs(detail: IProcessDetailResponse): void {
		detail.activity = [];
		if (detail.general?.init_operation) {
			// INICIO PROCESO
			detail.activity.push({
				title: this.trans.instant('PROCESSES.DETAIL.INIT_PROCESS'),
				date: detail.general?.init_operation,
				showDeveloperCurl: true,
				...(detail.logs_url ? { showLogs: true } : {}),
			});
		}
		const unifiedPolicies = buildUnifiedPolicyItems(detail, this.langSvc);
		if (unifiedPolicies.length) {
			detail.activity.push({
				section: true,
				title: this.trans.instant('PROCESSES.DETAIL.POLICIES')?.toUpperCase(),
			});
			unifiedPolicies.forEach((policy) => {
				detail.activity.push({
					title: policy.htmlResolved,
					icon: 'icon-shield',
					status: policy.accepted ? BOOLEAN_STATUS.OK : BOOLEAN_STATUS.KO,
					description: [
						policy.accepted
							? this.trans.instant('GENERAL.YES')?.toUpperCase()
							: this.trans.instant('GENERAL.NO')?.toUpperCase(),
					],
					link: {
						label: this.trans.instant('GENERAL.SHOW_DETAILS'),
						action: MenuItemAction.SHOW,
						info: policy.modalInfo,
					},
				});
			});
		}

		// Operaciones en el mismo orden que timeline (`Object.keys(detail.data)`).
		for (const operationKey of Object.keys(detail?.data ?? {})) {
			if (operationKey === 'HOME' || operationKey === 'FINISH') {
				continue;
			}
			if (!(detail.data as Record<string, unknown>)[operationKey]) {
				continue;
			}
			switch (operationKey) {
				case OPERATION_TYPE.OCR: {
					if (!detail.data.OCR) {
						break;
					}
					if (this.operationNeverStarted(detail.data.OCR)) {
						this.pushActivitySectionAndNotStarted(detail, 'PROCESSES.DETAIL.OCR', {
							operation: detail.data.OCR,
							linkInfoTitleKey: 'PROCESSES.DETAIL.OCR',
							state: detail.data.OCR.state,
							date: detail.data.OCR.end_at,
							showLogs: true,
						});
					} else {
						if (detail.data.OCR.start_at) {
							detail.activity.push({
								section: true,
								title: this.trans.instant('PROCESSES.DETAIL.OCR')?.toUpperCase(),
							});
						}
						let koGeneral: { title: string; subtitle: string } | null = null;
						if (this.shouldShowOperationKoReason(detail.data.OCR)) {
							const ocrError = detail.data.OCR.actions?.KO?.details?.error;
							koGeneral = this.detailKoGeneral(ocrError?.error_code, OPERATION_TYPE.OCR, undefined, {
								title: ocrError?.title,
								message: ocrError?.message,
								match_data_errors: ocrError?.match_data_errors,
							});
						}
						appendOcrActivityLogs(
							detail.activity,
							detail.data.OCR,
							{
								startAt: detail.data.OCR.start_at,
								endAt: detail.data.OCR.end_at,
								blockedIdentifiersCount: detail.locks?.blocked_identifiers?.length ?? 0,
								koGeneral,
								includeAmlPepResultDescription: true,
								includeFraudIdBlockDescription: true,
								screenphotoTitleKey: 'PROCESSES.DETAIL.SCREENPHOTO',
								photocopyTitleKey: 'PROCESSES.DETAIL.PHOTOCOPY',
								manipulatedPhotoTitleKey: 'PROCESSES.DETAIL.MANIPULATED_PHOTO',
							},
							{
								translate: (key, params) => this.trans.instant(key, params),
								getTagStatus: (state) => this.masterService.getTagStatus(state),
								shouldDisplayAntifraudCheck: (state, status, options) =>
									this.shouldDisplayAntifraudCheck(state, status, options),
								canShowOcrImageFraudChecks: () => this.canShowOcrImageFraudChecks(),
								getDocumentTypeLabel: (documentType) =>
									this.masterService.hasTranslation(`MASTER.DOCUMENT_TYPE.${documentType}`)
										? this.trans.instant(`MASTER.DOCUMENT_TYPE.${documentType}`)
										: '',
								getCountryLabel: (country) =>
									this.masterService.getNameCountry(country) || notAvailableLabel(this.trans),
								permanentExpirationLabel: () => permanentExpirationLabel(this.trans),
							},
						);
					}
					break;
				}
				case OPERATION_TYPE.POL: {
					if (!detail.data.POL) {
						break;
					}
					if (this.operationNeverStarted(detail.data.POL)) {
						this.pushActivitySectionAndNotStarted(detail, 'PROCESSES.DETAIL.POL', {
							operation: detail.data.POL,
							linkInfoTitleKey: 'PROCESSES.DETAIL.POL',
							state: detail.data.POL.state,
							date: detail.data.POL.end_at,
						});
					} else {
						if (detail.data.POL.start_at) {
							detail.activity.push({
								section: true,
								title: this.trans.instant('PROCESSES.DETAIL.POL')?.toUpperCase(),
							});
						}
						let koGeneral: { title: string; subtitle: string } | null = null;
						if (this.shouldShowOperationKoReason(detail.data.POL)) {
							const polError = detail.data.POL.actions?.KO?.details?.error;
							koGeneral = this.detailKoGeneral(
								polError?.error_code,
								OPERATION_TYPE.POL,
								detail.data.POL.data,
								{ title: polError?.title, message: polError?.message },
							);
						}
						appendPolActivityLogs(
							detail.activity,
							detail.data.POL,
							{
								startAt: detail.data.POL.start_at,
								endAt: detail.data.POL.end_at,
								blockedFacesCount: detail.locks?.blocked_faces?.length ?? 0,
								koGeneral,
								includeGeneralResult: true,
								includeAntispoof: true,
								includeAntispoofLab: true,
								includeFraudFaceBlockDescription: true,
								facialRecognitionTitleKey: 'PROCESSES.DETAIL.FACIAL_RECOGNITION',
							},
							{
								translate: (key, params) => this.trans.instant(key, params),
								getTagStatus: (state) => this.masterService.getTagStatus(state),
								shouldDisplayAntifraudCheck: (state, status, options) =>
									this.shouldDisplayAntifraudCheck(state, status, options),
								isPolGeneralResultOk: (general) => this.isPolGeneralResultOk(general),
								isSuperadmin: () => this.rolePermissions.isSuperadmin(),
							},
						);
					}
					break;
				}
				case OPERATION_TYPE.DOCUMENTS: {
					if (!detail.data.DOCUMENTS) {
						break;
					}
					if (this.operationNeverStarted(detail.data.DOCUMENTS)) {
						this.pushActivitySectionAndNotStarted(detail, 'PROCESSES.DETAIL.DOCUMENTS', {
							operation: detail.data.DOCUMENTS,
							linkInfoTitleKey: 'PROCESSES.DETAIL.DOCUMENTS',
							state: detail.data.DOCUMENTS.state,
							date: detail.data.DOCUMENTS.end_at,
						});
					} else {
						detail.activity.push({
							section: true,
							title: this.trans.instant('PROCESSES.DETAIL.DOCUMENTS')?.toUpperCase(),
						});
						appendDocumentsActivityLogs(
							detail.activity,
							detail.data.DOCUMENTS,
							{
								startAt: detail.data.DOCUMENTS.start_at,
								endAt: detail.data.DOCUMENTS.end_at,
							},
							{
								translate: (key, params) => this.trans.instant(key, params),
								hasTranslation: (key) => this.masterService.hasTranslation(key),
								getTagStatus: (state) => this.masterService.getTagStatus(state),
							},
						);
					}
					break;
				}
				case OPERATION_TYPE.FORM: {
					this.setFormActivityLogs(detail);
					break;
				}
				case OPERATION_TYPE.EVALUATION: {
					const evaluation = detail.data.EVALUATION;
					if (!evaluation) {
						break;
					}
					const isAttorneyPowers = evaluation.type?.toUpperCase() === CONNECTION_TYPE.SCRIPTURE;
					const evaluationSectionKey = isAttorneyPowers
						? 'WORKFLOWS.FORM.OPERATIONS.ATTORNEY_POWERS'
						: 'PROCESSES.DETAIL.EVALUATION';
					if (this.operationNeverStarted(evaluation)) {
						this.pushActivitySectionAndNotStarted(detail, evaluationSectionKey, {
							operation: evaluation,
							linkInfoTitleKey: evaluationSectionKey,
							state: evaluation.state,
							date: evaluation.end_at,
						});
					} else {
						detail.activity.push({
							section: true,
							title: this.trans.instant(evaluationSectionKey)?.toUpperCase(),
						});
						if (isAttorneyPowers) {
							if (evaluation.start_at) {
								detail.activity.push({
									date: evaluation.start_at,
									description: [this.trans.instant('PROCESSES.DETAIL.INIT_OPERATION')],
								});
							}
							this.pushAttorneyPowersAttemptActivityRows(detail, evaluation);
							if (evaluation.end_at) {
								detail.activity.push({
									description: [this.trans.instant('PROCESSES.DETAIL.FINISH_SCAN')],
									tag: evaluation.state
										? {
												label: this.trans.instant(
													`MASTER.STATUS.${evaluation.state.toUpperCase()}`,
												),
												status: this.masterService.getTagStatus(evaluation.state),
											}
										: undefined,
									date: evaluation.end_at,
									link: {
										label: this.trans.instant('GENERAL.SHOW_RESPONSE'),
										action: MenuItemAction.SHOW_JSON,
										info: {
											title: this.trans.instant(evaluationSectionKey),
											data: evaluation,
										},
									},
								});
							}
						} else {
							appendFinancialEvaluationActivityLogs(
								detail.activity,
								evaluation,
								{
									startAt: evaluation.start_at,
									endAt: evaluation.end_at,
									responseTitleKey: evaluationSectionKey,
								},
								{
									translate: (key, params) => this.trans.instant(key, params),
									hasTranslation: (key) => this.masterService.hasTranslation(key),
									getTagStatus: (state) => this.masterService.getTagStatus(state),
									notAvailableLabel: () => notAvailableLabel(this.trans),
								},
							);
						}
					}
					break;
				}
				case OPERATION_TYPE.ATTORNEY_POWERS: {
					const evaluation = detail.data.ATTORNEY_POWERS;
					if (!evaluation) {
						break;
					}
					const evaluationSectionKey = 'WORKFLOWS.FORM.OPERATIONS.ATTORNEY_POWERS';
					if (this.operationNeverStarted(evaluation)) {
						this.pushActivitySectionAndNotStarted(detail, evaluationSectionKey, {
							operation: evaluation,
							linkInfoTitleKey: evaluationSectionKey,
							state: evaluation.state,
							date: evaluation.end_at,
						});
					} else {
						detail.activity.push({
							section: true,
							title: this.trans.instant(evaluationSectionKey)?.toUpperCase(),
						});
						if (evaluation.start_at) {
							detail.activity.push({
								date: evaluation?.start_at,
								description: [this.trans.instant('PROCESSES.DETAIL.INIT_OPERATION')],
							});
						}
						this.pushAttorneyPowersAttemptActivityRows(detail, evaluation);
						if (evaluation?.end_at) {
							detail.activity.push({
								description: [this.trans.instant('PROCESSES.DETAIL.FINISH_SCAN')],
								tag: evaluation.state
									? {
											label: this.trans.instant(
												`MASTER.STATUS.${evaluation.state?.toUpperCase()}`,
											),
											status: this.masterService.getTagStatus(evaluation.state),
										}
									: undefined,
								date: evaluation?.end_at,
								link: {
									label: this.trans.instant('GENERAL.SHOW_RESPONSE'),
									action: MenuItemAction.SHOW_JSON,
									info: {
										title: this.trans.instant(evaluationSectionKey),
										data: evaluation,
									},
								},
							});
						}
					}
					break;
				}
				default:
					break;
			}
		}
		// FIN PROCESO
		if (detail.general?.end_operation) {
			detail.activity.push({
				section: true,
				title: this.trans.instant('PROCESSES.DETAIL.RESULT')?.toUpperCase(),
			});
			detail.activity.push({
				description: [this.trans.instant('PROCESSES.DETAIL.FINISH_PROCESS')],
				tag: {
					label: this.resolveProcessStatusLabel(
						detail.status_process,
						detail.process_status_code,
					),
					status: this.masterService.getTagStatus(detail.status_process),
				},
				date: detail.general?.end_operation,
			});
		}
	}

	/**
	 * NIF-944: el status del proceso es el de siempre (KO → Rechazado).
	 * `details_code` 3099 es motivo de KO (catálogo), no un status distinto.
	 */
	public resolveProcessStatusLabel(
		status: string | PROCESS_STATUS | null | undefined,
		_detailsCode?: number | null,
	): string {
		const key = (status ?? '').toString().toUpperCase();
		return this.trans.instant(`MASTER.STATUS.${key}`);
	}

	/**
	 * Actividad FORM en detalle de proceso (Figma `9257-173875`).
	 * Usa `data.FORM.step_activity[]` del back o deriva pasos desde `answers[]`.
	 */
	private setFormActivityLogs(detail: IProcessDetailResponse): void {
		const form = detail.data?.FORM as IOperationFormDetail | undefined;
		if (!form) {
			return;
		}

		if (this.operationNeverStarted({ start_at: form.start_at })) {
			this.pushActivitySectionAndNotStarted(detail, 'PROCESSES.DETAIL.FORM', {
				operation: form,
				linkInfoTitleKey: 'OPERATIONS.DETAIL.FORM.ACTIVITY_JSON_TITLE',
				state: form.state as OPERATION_STATUS,
				date: form.end_at,
				showLogs: true,
			});
			return;
		}

		detail.activity.push({
			section: true,
			title: this.trans.instant('PROCESSES.DETAIL.FORM')?.toUpperCase(),
		});

		appendFormOperationActivityLogs(
			detail.activity,
			form,
			{
				startAt: form.start_at,
				endAt: form.end_at,
				topLevelState: form.state,
				statusReasons: form.status_reasons ?? detail.status_reasons,
				logsUrl: detail.logs_url,
				locale: this.langSvc.getLanguage(),
			},
			{
				translate: (key, params) => this.trans.instant(key, params),
				shouldDisplayAntifraudCheck: (state, status, options) =>
					this.shouldDisplayAntifraudCheck(state, status, options),
				getTagStatus: (state) => this.masterService.getTagStatus(state),
			},
		);
	}

	/**
	 * Motivo KO de operación (`actions.KO`): visible si hay payload KO, sin filtrar por estado
	 * (incl. PENDING/OK tras revisión manual que conserva `actions.KO`).
	 */
	public shouldShowOperationKoReason(
		op?: { state?: string; actions?: { KO?: unknown } } | null,
	): boolean {
		return shouldShowOperationKoReasonUtil(op);
	}

	/**
	 * Indica si un check antifraud debe mostrarse en UI o actividad.
	 * Se basa solo en el status del check (NIF-819: no ocultar KO aunque la op esté OK).
	 * `alwaysShowResult` se mantiene por compatibilidad de firma (ya no filtra por estado de op).
	 */
	public shouldDisplayAntifraudCheck(
		_operationState: string | undefined,
		antifraudStatus: BOOLEAN_STATUS | string | undefined,
		options?: { showOnlyKo?: boolean; alwaysShowResult?: boolean },
	): boolean {
		return shouldDisplayAntifraudCheckUtil(_operationState, antifraudStatus, options);
	}

	/**
	 * Fraude visual OCR (screen/photocopy/manipulated): visible en int/pre;
	 * en producción solo superadmin (NIF-817, estilo laboratory).
	 */
	public canShowOcrImageFraudChecks(): boolean {
		return !environment.production || this.rolePermissions.isSuperadmin();
	}

	/** Algún check de comparación OCR (MRZ/AEAT/AML/PEP) visible según estado final de operación. */
	public hasDisplayableOcrCompareAntifraud(ocr: IOperationOcrDetail | null | undefined): boolean {
		return hasDisplayableOcrCompareAntifraudUtil(ocr);
	}

	/**
	 * Clave i18n si la operación OCR tiene mensaje de cierre de flujo conocido (`details` en cadena o `actions.KO`).
	 * No mezcla con POL: permite mostrar KO «de negocio» en OCR y el resto de errores de POL con normalidad.
	 */
	public getKoCompletionExclusiveMessageForOcr(detail: IProcessDetailResponse | null | undefined): string | null {
		return this.koCompletionExclusiveKeyForOperation(detail?.data?.OCR);
	}

	/** Igual que OCR pero solo para la operación POL. */
	public getKoCompletionExclusiveMessageForPol(detail: IProcessDetailResponse | null | undefined): string | null {
		return this.koCompletionExclusiveKeyForOperation(detail?.data?.POL);
	}

	private koCompletionExclusiveKeyForOperation(
		op:
			| {
					details?: unknown;
					actions?: { KO?: { details?: { error?: { msg?: string; error_code?: number } } } };
			  }
			| undefined
			| null,
	): string | null {
		if (!op) return null;
		// Si el back trae `error_code` específico (p. ej. 2047 permisos), gana sobre cualquier
		// mensaje genérico de cascada que pueda venir además en `op.details` o
		// `actions.KO.details.error.msg`: cedemos a `detailKoGeneral`, que sí muestra
		// TITLE_/SUBTITLE_${code}. Sin esto, `op.details` con el texto de cascada se
		// resolvía antes y enmascaraba el código real (caso típico: 2047 + cierre del proceso).
		if (op.actions?.KO?.details?.error?.error_code) {
			return null;
		}
		const keyFor = (raw: string | undefined | null): string | null => {
			if (typeof raw !== 'string') return null;
			const t = raw.trim();
			return t ? (KO_COMPLETION_BACKEND_TO_I18N[t] ?? null) : null;
		};
		if (typeof op.details === 'string') {
			const fromDetails = keyFor(op.details);
			if (fromDetails) return fromDetails;
		}
		return keyFor(op.actions?.KO?.details?.error?.msg);
	}

	public detailKoGeneral(
		code: number,
		operation?: OPERATION_TYPE,
		polData?: IOperationPolDataTry[],
		payload?: {
			title?: string;
			message?: string;
			msg?: string;
			/** NIF-1101: keys de cotejo; copy vía i18n (no confiar en title genérico del catálogo). */
			match_data_errors?: Array<{ key?: string | null }> | null;
		} | null,
	): { title: string; subtitle: string } {
		const withOperationPrefix = (title: string): string => {
			if (operation === OPERATION_TYPE.OCR) {
				return `${this.trans.instant('PROCESSES.DETAIL.KO_ERROR_TITLE_PREFIX_OCR')}${title}`;
			}
			if (operation === OPERATION_TYPE.POL) {
				return `${this.trans.instant('PROCESSES.DETAIL.KO_ERROR_TITLE_PREFIX_POL')}${title}`;
			}
			if (operation === OPERATION_TYPE.FORM) {
				return `${this.trans.instant('PROCESSES.DETAIL.KO_ERROR_TITLE_PREFIX_FORM')}${title}`;
			}
			if (operation === OPERATION_TYPE.ATTORNEY_POWERS) {
				return `${this.trans.instant('PROCESSES.DETAIL.KO_ERROR_TITLE_PREFIX_ATTORNEY_POWERS')}${title}`;
			}
			if (operation === OPERATION_TYPE.DOCUMENTS) {
				return `${this.trans.instant('PROCESSES.DETAIL.KO_ERROR_TITLE_PREFIX_DOCUMENTS')}${title}`;
			}
			return title;
		};

		if (code) {
			// NIF-1101: campo concreto (match_data_errors o parse de msg técnico) — no title legacy catálogo.
			if (code === 2014) {
				const fieldMsg = resolveMatchDataUserMessageFromKoError(
					payload,
					(key, params) => this.trans.instant(key, params),
					(key) => this.masterService.hasTranslation(key),
				);
				if (fieldMsg) {
					return { title: withOperationPrefix(fieldMsg), subtitle: fieldMsg };
				}
			}

			const payloadTitle = payload?.title?.trim() || '';
			const payloadMessage = payload?.message?.trim() || '';
			if (payloadTitle || payloadMessage) {
				let subtitle = payloadMessage || payloadTitle;
				const polRow = polData?.[2];
				if (operation === OPERATION_TYPE.POL && code === 2020 && isPolDataRowKoMultipleFaces(polRow)) {
					subtitle = this.trans.instant(`ERRORS.PROCESS.SUBTITLE_${code}_2`);
				}
				return {
					title: withOperationPrefix(payloadTitle || payloadMessage),
					subtitle,
				};
			}

			// Sin title/message en payload (API antigua): i18n; no disparar listado de 2000.
			const locale = this.langSvc.currentLang() || 'es';
			const { title, subtitle: baseSubtitle } = this.errorsNumbersService.resolveCopy(
				code,
				locale,
				(key) => this.trans.instant(key),
				{
					titleKey: this.resolveMatchDataErrorTitleKey(code, operation),
					subtitleKey: this.resolveMatchDataErrorSubtitleKey(code, operation),
				},
			);
			let subtitle = baseSubtitle;
			const polRow = polData?.[2];
			if (operation === OPERATION_TYPE.POL && code === 2020 && isPolDataRowKoMultipleFaces(polRow)) {
				subtitle = this.trans.instant(`ERRORS.PROCESS.SUBTITLE_${code}_2`);
			}
			if (title || subtitle) {
				return { title: withOperationPrefix(title), subtitle };
			}
		}
		return {
			title: withOperationPrefix(this.trans.instant('ERRORS.PROCESS.TITLE_GENERIC')),
			subtitle: this.trans.instant('ERRORS.PROCESS.SUBTITLE_GENERIC'),
		};
	}

	/** Mensaje KO de cotejo match_data a nivel proceso (`process_status_code` 2014). */
	public resolveProcessMatchDataKoMessage(
		detail: IProcessDetailResponse | null | undefined,
	): { title: string; subtitle: string } | null {
		if (!detail || detail.status_process !== PROCESS_STATUS.KO) {
			return null;
		}
		const code = this.resolveProcessMatchDataErrorCode(detail);
		if (code !== 2014) {
			return null;
		}
		const formOperation = hasFormOperationMatchDataKo(detail.data?.FORM) ? OPERATION_TYPE.FORM : undefined;
		return this.detailKoGeneral(2014, formOperation);
	}

	/** 2014 FORM → copy formulario; resto → auth/referencia (sin mencionar formulario). */
	private resolveMatchDataErrorTitleKey(code: number, operation?: OPERATION_TYPE): string {
		if (code !== 2014) {
			return `ERRORS.PROCESS.TITLE_${code}`;
		}
		if (operation === OPERATION_TYPE.FORM) {
			return `ERRORS.PROCESS.TITLE_${code}_MATCH_DATA`;
		}
		return `ERRORS.PROCESS.TITLE_${code}_AUTH_MATCH_DATA`;
	}

	private resolveMatchDataErrorSubtitleKey(code: number, operation?: OPERATION_TYPE): string {
		if (code !== 2014) {
			return `ERRORS.PROCESS.SUBTITLE_${code}`;
		}
		if (operation === OPERATION_TYPE.FORM) {
			return `ERRORS.PROCESS.SUBTITLE_${code}_MATCH_DATA`;
		}
		return `ERRORS.PROCESS.SUBTITLE_${code}_AUTH_MATCH_DATA`;
	}

	public resolveProcessMatchDataErrorCode(detail: IProcessDetailResponse | null | undefined): number | null {
		if (!detail) {
			return null;
		}
		if (detail.process_status_code === 2014) {
			return 2014;
		}
		if (detail.process_details?.trim() === 'Error in MatchData') {
			return 2014;
		}
		return null;
	}

	public resetFilters(): void {
		this.filterValues.set({});
		this.metadataKeys.set([]);
		this.availableKoErrorCodes.set([]);
		this.loadWorkflowOptions();
	}

	public loadWorkflowOptions(companyUuid?: string | string[] | null): void {
		if (this.workflowsCatalog().length > 0) {
			this.applyWorkflowFilter(companyUuid);
			return;
		}

		this.pendingWorkflowCompanyUuid = companyUuid;
		if (this.workflowsCatalogLoading) {
			return;
		}

		this.workflowsCatalogLoading = true;
		this.workflowsService
			.list({
				unpaginated: true,
				sort_by: 'alias',
				sort_order: 'asc',
			})
			.subscribe({
				next: (response) => {
					this.workflowsCatalog.set(
						(response.data ?? []).map((workflow: IWorkflowsListBody) => ({
							id: workflow.uuid,
							label: workflow.alias || workflow.uuid,
							company_uuid: workflow.company_uuid,
						})),
					);
					this.applyWorkflowFilter(this.pendingWorkflowCompanyUuid);
				},
				error: () => {
					this.workflowsCatalogLoading = false;
				},
				complete: () => {
					this.workflowsCatalogLoading = false;
				},
			});
	}

	private normalizeCompanyUuidFilter(companyUuid?: string | string[] | null): string[] {
		if (companyUuid == null) {
			return [];
		}
		const arr = Array.isArray(companyUuid) ? companyUuid : [companyUuid];
		return arr.map((id) => String(id).trim()).filter((id) => id !== '');
	}

	private applyWorkflowFilter(companyUuid?: string | string[] | null): void {
		const companyIds = this.normalizeCompanyUuidFilter(companyUuid);
		const catalog = this.workflowsCatalog();
		const filtered =
			companyIds.length > 0
				? catalog.filter((w) => w.company_uuid != null && companyIds.includes(String(w.company_uuid)))
				: catalog;
		this.workflowOptions.set(filtered.map(({ id, label }) => ({ id, label })));
	}

	public setAvailableKoErrorCodes(codes: number[] | undefined): void {
		this.availableKoErrorCodes.set(codes ?? []);
	}

	public getFilterValuesSnapshot(): Record<string, any> {
		return { ...this.filterValues() };
	}

	public setFilterValues(vals: Record<string, any>): void {
		this.filterValues.set(vals);
	}

	/** True si el filtro rápido de estado incluye Rechazado (KO). */
	public isKoSelectedInFilter(stateUuid: unknown): boolean {
		if (stateUuid == null) {
			return false;
		}
		const koOption = this.masterService.processStatusFilter?.find((s) => s.value === PROCESS_STATUS.KO);
		const koUuid = koOption ? String(koOption.id) : '';
		const arr = Array.isArray(stateUuid) ? stateUuid : [stateUuid];
		return arr.some((v) => {
			if (v?.value === PROCESS_STATUS.KO || v === PROCESS_STATUS.KO) {
				return true;
			}
			const id = String(v?.id ?? v);
			if (id === PROCESS_STATUS.KO || (koUuid && id === koUuid)) {
				return true;
			}
			const option = this.masterService.processStatusFilter?.find((s) => String(s.id) === id);
			return option?.value === PROCESS_STATUS.KO;
		});
	}

	/** Opciones del filtro de códigos KO en listado: sí necesita catálogo (inventario). */
	public getProcessErrorCodeOptions(codes?: number[]): ISelect[] {
		void this.errorsNumbersService.ensureCatalogLoaded().subscribe();
		const source = codes ?? this.availableKoErrorCodes();
		const locale = this.langSvc.currentLang() || 'es';
		const options: ISelect[] = [];

		for (const code of source) {
			const { title } = this.errorsNumbersService.resolveCopy(code, locale, (key) =>
				this.trans.instant(key),
			);
			const i18nKey = `ERRORS.PROCESS.TITLE_${code}`;
			if (title === i18nKey) {
				continue;
			}
			options.push({
				id: code,
				label: `${code} — ${title}`,
				value: String(code),
			});
		}

		return options.sort((a, b) => Number(a.id) - Number(b.id));
	}

	/**
	 * Restores toolbar filter display state from a saved payload (e.g. when returning from detail).
	 * Payload can contain raw ids or toolbar-style objects { id, label }; normalizes and resolves to option objects.
	 */
	public setFiltersFromPayload(payload: Record<string, any> | undefined): void {
		if (!payload || !Object.keys(payload).length) {
			return;
		}
		const idOf = (v: any): string | number => (v != null && typeof v === 'object' && 'id' in v ? v.id : v);
		const opts = (list: ISelect[] | undefined, id: string | number) =>
			list?.find((o) => String(o.id) === String(id));
		const toSelect = (list: ISelect[] | undefined, v: any): ISelect | undefined => {
			const id = idOf(v);
			if (id == null || id === '' || (typeof id === 'string' && id.trim() === '')) {
				return undefined;
			}
			const o = opts(list, id);
			return o ?? ({ id, label: typeof v === 'object' && v?.label != null ? v.label : String(id) } as ISelect);
		};
		const vals: Record<string, any> = {};

		// Filtros rápidos: state_uuid es multiple → tras formatFilterTable llega como array de ids.
		// Hay que mapear cada id a ISelect; toSelect con el array entero falla y el chip desaparecía al volver del detalle.
		if (isToolbarPayloadValuePresent(payload['state_uuid'])) {
			const raw = payload['state_uuid'];
			const arr = Array.isArray(raw) ? raw : [raw];
			const mapped = arr.map((v: any) => toSelect(this.masterService.processStatusFilter, v)).filter(Boolean);
			if (mapped.length) {
				vals['state_uuid'] = mapped;
			}
		}
		if (isToolbarPayloadValuePresent(payload['document_type'])) {
			const sel = toSelect(this.masterService.documentTypes, payload['document_type']);
			if (sel) vals['document_type'] = sel;
		}
		if (isToolbarPayloadValuePresent(payload['nationality'])) {
			const sel = toSelect(this.masterService.countries, payload['nationality']);
			if (sel) vals['nationality'] = sel;
		}
		if (isToolbarPayloadValuePresent(payload['start_at'])) vals['start_at'] = payload['start_at'];
		if (isToolbarPayloadValuePresent(payload['end_at'])) vals['end_at'] = payload['end_at'];
		if (isToolbarPayloadValuePresent(payload['operation_type_uuids'])) {
			const raw = payload['operation_type_uuids'];
			const arr = Array.isArray(raw) ? raw : [raw];
			const mapped = arr.map((v: any) => toSelect(this.masterService.operationsTypes, v)).filter(Boolean);
			if (mapped.length) vals['operation_type_uuids'] = mapped;
		}
		if (isToolbarPayloadValuePresent(payload['browser_uuids'])) {
			const raw = payload['browser_uuids'];
			const arr = Array.isArray(raw) ? raw : [raw];
			const mapped = arr.map((v: any) => toSelect(this.masterService.browsers, v)).filter(Boolean);
			if (mapped.length) vals['browser_uuids'] = mapped;
		}
		if (isToolbarPayloadValuePresent(payload['device_type_uuids'])) {
			const raw = payload['device_type_uuids'];
			const arr = Array.isArray(raw) ? raw : [raw];
			const mapped = arr.map((v: any) => toSelect(this.masterService.deviceTypes, v)).filter(Boolean);
			if (mapped.length) vals['device_type_uuids'] = mapped;
		}
		if (isToolbarPayloadValuePresent(payload['process_uuid'])) vals['process_uuid'] = payload['process_uuid'];
		if (isToolbarPayloadValuePresent(payload['authorization_uuid']))
			vals['authorization_uuid'] = payload['authorization_uuid'];
		if (isToolbarPayloadValuePresent(payload['document_number']))
			vals['document_number'] = payload['document_number'];
		if (isToolbarPayloadValuePresent(payload['metadata_key'])) {
			const sel = toSelect(this.metadataKeys(), payload['metadata_key']);
			if (sel) vals['metadata_key'] = sel;
		}
		if (isToolbarPayloadValuePresent(payload['metadata_value'])) vals['metadata_value'] = payload['metadata_value'];
		if (isToolbarPayloadValuePresent(payload['company_uuid'])) {
			const raw = payload['company_uuid'];
			const arr = Array.isArray(raw) ? raw : [raw];
			const mapped = arr.map((v: any) => toSelect(this.masterService.companies, v)).filter(Boolean);
			if (mapped.length) vals['company_uuid'] = mapped;
		}
		if (isToolbarPayloadValuePresent(payload['workflow_uuid'])) {
			const sel = toSelect(this.workflowOptions(), payload['workflow_uuid']);
			if (sel) {
				vals['workflow_uuid'] = sel;
			} else {
				const id = idOf(payload['workflow_uuid']);
				vals['workflow_uuid'] = {
					id,
					label:
						typeof payload['workflow_uuid'] === 'object' && payload['workflow_uuid']?.label != null
							? payload['workflow_uuid'].label
							: String(id),
				} as ISelect;
			}
		}
		if (isToolbarPayloadValuePresent(payload['error_code'])) {
			const code = Number(idOf(payload['error_code']));
			const sel = toSelect(this.getProcessErrorCodeOptions([code]), payload['error_code']);
			if (sel) vals['error_code'] = sel;
		}

		this.filterValues.set(vals);
		// Carga de metadata/workflows: la hace process-list.ngOnInit una sola vez tras restaurar.
	}

	public updateMetadataKeys(companyUuid: string | null, currentFilters: Record<string, any> = {}): void {
		if (!companyUuid) {
			this.metadataKeys.set([]);
			return;
		}
		this.companiesService.getMetadata(companyUuid).subscribe((metadata) => {
			// Set filter values and new options together so the computed
			// rebuilds once restoring all previous values alongside new options.
			this.filterValues.set(currentFilters);
			this.metadataKeys.set(
				metadata.data?.map((meta) => ({
					id: meta.uuid,
					label: meta.name,
				})) || [],
			);
		});
	}

	/**
	 * Indica que el OCR marcó necesidad de certificado/NIE (p. ej. pasaporte extranjero + carta verde).
	 * En ese caso no aplicamos KO por tipo/país frente a allowed_docs: el proceso contempla otro doc. posterior.
	 */
	private isRequiredNieFlag(ocr: IOperationOcrDetail | undefined): boolean {
		const v = ocr?.required_nie;
		if (v === true) {
			return true;
		}
		if (typeof v === 'string') {
			return v === 'true';
		}
		return false;
	}

	public checkExpirationDate(ocr: IOperationOcrDetail): void {
		if (ocr?.expiration_date) {
			const expirationDate = transformDate(ocr?.expiration_date);
			if (
				expirationDate &&
				expirationDate < new Date() &&
				ocr?.antifraud?.EXPIRATED === 'OK' &&
				ocr?.actions?.EXPIRATED?.details
			) {
				ocr.actions.EXPIRATED.showModal = true;
			}
		}
	}

	public checkTypeDocument(ocr: IOperationOcrDetail): void {
		if (!ocr?.allowed_docs || !ocr.antifraud) {
			return;
		}
		if (this.isRequiredNieFlag(ocr)) {
			ocr.antifraud.DOCUMENT_TYPE = BOOLEAN_STATUS.OK;
			return;
		}
		let result: BOOLEAN_STATUS;
		let docType = ocr?.document_type;
		if (docType === 'TARJ_RESID') {
			docType = 'nie';
		}
		let availableTypes: Array<'dni' | 'passport' | 'nie'> = [];
		if (ocr.allowed_docs.dni) {
			availableTypes.push('dni');
		}
		if (ocr.allowed_docs.nie) {
			availableTypes.push('nie');
		}
		if (ocr.allowed_docs.passport) {
			availableTypes.push('passport');
		}
		if (docType && availableTypes.length) {
			result = availableTypes.some((type) => docType.toLowerCase().includes(type.toLowerCase()))
				? BOOLEAN_STATUS.OK
				: BOOLEAN_STATUS.KO;
		}
		ocr.antifraud.DOCUMENT_TYPE = result;
	}

	public checkCountry(ocr: IOperationOcrDetail): void {
		if (!ocr?.allowed_docs || !ocr.antifraud) {
			return;
		}
		if (this.isRequiredNieFlag(ocr)) {
			ocr.antifraud.COUNTRY = BOOLEAN_STATUS.OK;
			return;
		}
		let result: BOOLEAN_STATUS;
		const country = ocr?.country;
		const docType = ocr?.document_type;
		const allowedCountries = ocr.allowed_docs;
		const notAllowedCountries = ocr?.not_allowed_docs;
		let allowedCountriesDni;
		let allowedCountriesPassport;
		// DNI
		if (allowedCountries.dni) {
			if (allowedCountries.dni.length === 0) {
				allowedCountriesDni = this.masterService.countries.map((c: ISelect) => c.id);
			} else {
				allowedCountriesDni = allowedCountries.dni;
			}
		}
		if (notAllowedCountries?.dni && notAllowedCountries?.dni?.length) {
			allowedCountriesDni = this.masterService.countries
				.filter((c: ISelect) => notAllowedCountries?.dni.includes(c))
				.map((c: ISelect) => c.id);
		}
		// PASSPORT
		if (allowedCountries.passport) {
			if (allowedCountries.passport.length === 0) {
				allowedCountriesPassport = this.masterService.countries.map((c: ISelect) => c.id);
			} else {
				allowedCountriesPassport = allowedCountries.passport;
			}
		}
		if (notAllowedCountries?.passport && notAllowedCountries?.passport?.length) {
			allowedCountriesPassport = this.masterService.countries
				.filter((c: ISelect) => notAllowedCountries?.passport.includes(c))
				.map((c: ISelect) => c.id);
		}
		if (docType?.toLowerCase().includes('dni')) {
			result = allowedCountriesDni?.includes(country) ? BOOLEAN_STATUS.OK : BOOLEAN_STATUS.KO;
		} else if (docType?.toLowerCase().includes('passport')) {
			result = allowedCountriesPassport?.includes(country) ? BOOLEAN_STATUS.OK : BOOLEAN_STATUS.KO;
		} else if (docType?.toLowerCase().includes('nie')) {
			result = country === 'ESP' ? BOOLEAN_STATUS.OK : BOOLEAN_STATUS.KO;
		}
		ocr.antifraud.COUNTRY = result;
	}

	/**
	 * Comprueba si la edad del documento está en el rango POL (±10).
	 * NIF-856: POL lite (`view=initial`) puede traer high_age/low_age sin `antifraud`.
	 */
	public checkRangeAge(pol: IOperationPolDetail): void {
		const rangeAge: number = 10;
		if (!pol?.high_age || !pol?.low_age) {
			return;
		}
		if (!pol.antifraud) {
			pol.antifraud = {};
		}
		pol.antifraud.RANGE_AGE =
			this.yearsOld >= parseInt(pol.low_age) - rangeAge &&
			this.yearsOld <= parseInt(pol.high_age) + rangeAge
				? BOOLEAN_STATUS.OK
				: BOOLEAN_STATUS.KO;
	}

	/**
	 * Calcula la edad a mostrar en detalle de proceso.
	 * Prioriza birthdate OCR; si falta (p. ej. solo identity en view=initial), usa fallback.
	 * NIF-813: no depende de allowed_docs/antifraud (ausentes en OCR lite).
	 */
	public setYearsOld(ocr?: IOperationOcrDetail | null, fallbackBirthdate?: string | null): void {
		const raw = ocr?.birthdate || fallbackBirthdate;
		if (raw) {
			const birthDate = transformDate(raw);
			if (birthDate) {
				const today = new Date();
				let age = today.getFullYear() - birthDate.getFullYear();
				const m = today.getMonth() - birthDate.getMonth();
				if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
					age--;
				}
				this.yearsOld = age;
				this.ocrDocumentAgeKnown = true;
				return;
			}
		}
		this.yearsOld = 0;
		this.ocrDocumentAgeKnown = false;
	}

	/**
	 * Fusiona un fragmento parcial del detalle (tab u operación) en la respuesta base.
	 */
	public mergeProcessDetail(
		base: IProcessDetailResponse,
		patch: Partial<IProcessDetailResponse>,
	): IProcessDetailResponse {
		const merged: IProcessDetailResponse = {
			...base,
			...patch,
			general: {
				...base.general,
				...(patch.general ?? {}),
			},
			data: {
				...base.data,
				...(patch.data ?? {}),
			},
		};

		if (patch.data) {
			for (const key of Object.keys(patch.data) as OPERATION_TYPE[]) {
				const patchOp = patch.data[key];
				if (!patchOp) {
					continue;
				}
				const baseOp = merged.data?.[key];
				merged.data[key] = baseOp ? { ...baseOp, ...patchOp } : patchOp;
			}
		}

		return merged;
	}
}
